import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { logoutUser } from "../../api/services/authService";
import { getLoginPageByRole } from "../../utils/authHelpers";
import StatusDot from "../common/StatusDot";
import { hasPermission } from "../../utils/permissions";
import { useTranslation } from "react-i18next";
import LanguageToggle from "../LanguageToggle";
import {
  LayoutDashboard,
  Box,
  Share2,
  Users,
  LogOut,
  ChevronRight,
  Activity,
  Cpu,
  Globe,
} from "lucide-react";

export const COMPANY_USER_NAV_LINKS = [
  { label: "Dashboard", icon: LayoutDashboard, path: "/home" },
  { label: "Boxes", icon: Box, path: "/boxes" },
  { label: "My Team", icon: Users, path: "/my-team" },
  { label: "Topology", icon: Share2, path: "/topology" },
];

export default function CompanyUserSidebar({ isCollapsed = false, onClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { i18n } = useTranslation();
  const toggleLanguage = () => {
    const newLang = i18n.language === "en" ? "ar" : "en";
    i18n.changeLanguage(newLang);
    localStorage.setItem("language", newLang);
    document.documentElement.dir = newLang === "ar" ? "rtl" : "ltr";
  };

  const navLinks = COMPANY_USER_NAV_LINKS.filter((link) => {
    if (link.path === "/boxes") {
      return hasPermission(user, "catalog_view");
    }
    if (link.path === "/topology") {
      return hasPermission(user, "topology_access");
    }
    if (link.path === "/my-team") {
      return Array.isArray(user?.team_ids) && user.team_ids.length > 0;
    }
    return true;
  });

  return (
    <>
      {!isCollapsed && (
        <div
          className="fixed inset-0 z-20 bg-black/50 backdrop-blur-sm lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        dir="ltr"
        className={`
         fixed top-16 sm:top-19 left-0 bottom-0 w-60 flex flex-col z-30
  qv-glass-sidebar
  max-lg:bg-(--color-bg)! max-lg:backdrop-blur-none!

        transition-[transform,width] duration-300 ease-in-out
        ${isCollapsed ? "-translate-x-full" : "translate-x-0"}
        lg:translate-x-0
        ${isCollapsed ? "lg:w-16" : "lg:w-60"}
      `}
      >
        {/* Nav links */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto overflow-x-hidden">
          <div
            className={`text-[9px] font-bold tracking-[0.2em] uppercase text-(--color-text-muted) px-3 mb-2 select-none ${isCollapsed ? "lg:hidden" : ""}`}
          >
            Navigation Menu
          </div>

          {navLinks.map(({ label, icon: Icon, path }) => {
            const isActive =
              path === "/home"
                ? location.pathname === "/home"
                : location.pathname.startsWith(path);

            return (
              <Link
                key={label}
                to={path}
                title={isCollapsed ? label : undefined}
                className={`
                  relative flex items-center justify-between px-3 py-2.5 rounded-sm text-[13px] font-medium
                  transition-all duration-200 group border border-transparent whitespace-nowrap
                  ${isCollapsed ? "lg:justify-center lg:px-2" : ""}
                  ${
                    isActive
                      ? "text-(--color-text-primary) bg-linear-to-r from-(--color-primary-100) to-(--color-primary-50) border-(--color-primary-100)/20"
                      : "text-(--color-text-secondary) hover:text-(--color-text-primary) hover:bg-slate-900/5 hover:translate-x-1"
                  }
                `}
              >
                <div
                  className={`flex items-center gap-3 ${isCollapsed ? "lg:gap-0" : ""}`}
                >
                  <Icon
                    size={17}
                    className={
                      isActive
                        ? "text-(--color-primary) shrink-0"
                        : "text-(--color-text-muted) group-hover:text-(--color-primary) transition-colors shrink-0"
                    }
                  />
                  <span className={isCollapsed ? "lg:hidden" : ""}>
                    {label}
                  </span>
                </div>
              </Link>
            );
          })}
        </nav>

        {/* Bottom actions & Profile Quick HUD */}
        <div className="p-3 border-t border-(--color-border) space-y-2.5">
          <div
            className={`sm:hidden flex items-center justify-between px-3 py-2 ${isCollapsed ? "lg:hidden" : ""}`}
          >
            <span className="text-xs text-(--color-text-muted) font-medium">
              Language
            </span>
            <LanguageToggle
              lang={i18n.language}
              toggleLanguage={toggleLanguage}
              standalone={false}
            />
          </div>
          <button
            title={isCollapsed ? "Logout" : undefined}
            onClick={async () => {
              setIsLoggingOut(true);
              try {
                await Promise.all([
                  logoutUser(),
                  new Promise((resolve) => setTimeout(resolve, 1000)),
                ]);
                const loginPage = getLoginPageByRole(user?.roles);
                logout();
                navigate(loginPage, { replace: true });
              } catch {
                const loginPage = getLoginPageByRole(user?.roles);
                logout();
                navigate(loginPage, { replace: true });
              } finally {
                setIsLoggingOut(false);
              }
            }}
            className={`w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-red-500 hover:bg-red-500/10 hover:text-red-400 border border-transparent hover:border-red-500/20 rounded-sm transition-all ${isCollapsed ? "lg:justify-center lg:px-2" : ""}`}>
            <span
              className={`flex items-center gap-2 ${isCollapsed ? "lg:gap-0" : ""}`}
            >
              <LogOut size={14} className="shrink-0" />
              <span className={isCollapsed ? "lg:hidden" : ""}>
                Logout System
              </span>
            </span>
            <ChevronRight
              size={12}
              className={`opacity-55 ${isCollapsed ? "lg:hidden" : ""}`}
            />
          </button>
        </div>
      </aside>

      {/* Logout full-screen overlay */}
      {isLoggingOut && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-9999">
          <div className="flex flex-col items-center gap-4">
            <div className="relative w-16 h-16">
              <div className="absolute inset-0 border-4 border-(--color-primary)/20 rounded-full" />
              <div className="absolute inset-0 border-4 border-transparent border-t-(--color-primary) rounded-full animate-spin" />
            </div>
            <p className="text-white font-semibold">Logging out…</p>
          </div>
        </div>
      )}
    </>
  );
}
